import React, { useState, useEffect, Suspense, lazy } from "react"
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom"
import { auth, firestore } from "./firebase"
import {
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
} from "firebase/auth"
import { doc, getDoc, onSnapshot } from "firebase/firestore"
import Header from "./components/non-ui/Header"
import Navigation from "./components/non-ui/Navigation"
import Footer from "./components/non-ui/Footer"
import { attachCurrentLandingTrialToUser } from "./utils/landingTryonJobs"
import { trackFunnel } from "./utils/trackFunnel"
import { ensureUserDoc } from "./utils/userDoc"
import "./App.css"
import useIsAdmin from "./hooks/useIsAdmin"
import { STUDIO_ENABLED } from "./utils/studioEnabled"

const Tryon = lazy(() => import("./pages/(logged-in)/tryon"))
const HomeScreen = lazy(() => import("./pages/homeScreen"))
const Pricing = lazy(() => import("./pages/Pricing"))
const Admin = lazy(() => import("./pages/Admin"))
const UploadTemplates = lazy(() => import("./pages/UploadTemplates"))
const Demo = lazy(() => import("./pages/Demo"))
const Demo2 = lazy(() => import("./pages/Demo2"))
const DemoManager = lazy(() => import("./pages/DemoManager"))
const Legal = lazy(() => import("./pages/Legal"))
const Api = lazy(() => import("./pages/Api"))
const Media = lazy(() => import("./pages/Media"))
const Studio = lazy(() => import("./pages/(logged-in)/studio"))
const StudioLab = lazy(() => import("./pages/(logged-in)/studioLab"))
const StudioLibrary = lazy(() => import("./pages/(logged-in)/studioLibrary"))
const StudioAbc = lazy(() => import("./pages/(logged-in)/studioAbc"))
const StudioPins = lazy(() => import("./pages/(logged-in)/studioPins"))
const StudioAb2 = lazy(() => import("./pages/(logged-in)/studioAb2"))
const StudioShoots = lazy(() => import("./pages/(logged-in)/studioShoots"))
const StudioSketch = lazy(() => import("./pages/(logged-in)/studioSketch"))
const StudioFocus = lazy(() => import("./pages/(logged-in)/studioFocus"))
const StudioReview = lazy(() => import("./pages/(logged-in)/studioReview"))
const StudioTry20 = lazy(() => import("./pages/(logged-in)/studioTry20"))
const StudioSwapTest = lazy(() => import("./pages/(logged-in)/studioSwapTest"))
const StudioFashion = lazy(() => import("./pages/(logged-in)/studioFashion"))
const StudioTemplatePick = lazy(() => import("./pages/(logged-in)/studioTemplatePick"))
const StudioHub = lazy(() => import("./pages/(logged-in)/studioHub"))
const StyleTinder = lazy(() => import("./pages/(logged-in)/styleTinder"))
// Studio is not released yet: every /studio page is admin-only until launch.
function AdminOnly({ children }) {
  const isAdmin = useIsAdmin()
  if (!STUDIO_ENABLED) return <Navigate to="/tryon" replace />
  if (isAdmin === null) return <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>Loading…</div>
  if (!isAdmin) return <Navigate to="/tryon" replace />
  return children
}
// Admin tools that may run on jewlai.com (not part of the unreleased Studio).
function AdminGate({ children }) {
  const isAdmin = useIsAdmin()
  if (isAdmin === null) return <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>Loading…</div>
  if (!isAdmin) return <Navigate to="/tryon" replace />
  return children
}
const lazyPage = (el) => <Suspense fallback={<div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>Loading…</div>}>{el}</Suspense>

function AppContent() {
  const location = useLocation();
  const [user, setUser] = useState(null)

  useEffect(() => {
    let unsubscribeFirestore = null

    const unsubscribeAuth = onAuthStateChanged(auth, (authUser) => {
      if (authUser) {
        // Fills balance/plan/createdAt for docs that were created without them.
        ensureUserDoc(authUser, { touchLogin: false }).catch((err) => {
          console.error("user doc check failed", err)
        })
        // User is signed in - subscribe to Firestore user data
        const userDocRef = doc(firestore, "users", authUser.uid)
        
        unsubscribeFirestore = onSnapshot(userDocRef, (docSnap) => {
          if (docSnap.exists()) {
            // Merge Firebase Auth data with Firestore data
            const firestoreData = docSnap.data()
            setUser({
              ...authUser,
              ...firestoreData,
              uid: authUser.uid, // Ensure uid is from auth
              email: authUser.email, // Ensure email is from auth
            })
          } else {
            // No Firestore doc yet, just set auth user
            setUser(authUser)
          }
        })
      } else {
        // User is signed out
        setUser(null)
        if (unsubscribeFirestore) {
          unsubscribeFirestore()
        }
      }
    })

    return () => {
      unsubscribeAuth()
      if (unsubscribeFirestore) {
        unsubscribeFirestore()
      }
    }
  }, [])

  const handleGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider()
    try {
      const result = await signInWithPopup(auth, provider)
      await configUser(result.user)
      setUser(result.user)
    } catch (error) {
      console.error("Error during Google sign-in:", error.message)
    }
  }

  const configUser = async (user) => {
    const userRef = doc(firestore, "users", user.uid)
    let created = false
    try {
      created = await ensureUserDoc(user)
    } catch (docErr) {
      console.error("user doc setup failed", docErr)
    }
    trackFunnel("signed_in", { from: created ? "new" : "existing" })
    try {
      await attachCurrentLandingTrialToUser(user.uid)
    } catch (linkErr) {
      console.error("landing user link failed", linkErr)
    }
    const nextSnap = await getDoc(userRef)
    return nextSnap.exists() ? nextSnap.data() : null
  }

  const handleSignOut = async () => {
    try {
      await signOut(auth)
      setUser(null)
    } catch (error) {
      console.error("Error during sign-out:", error.message)
    }
  }

  const isDemo = location.pathname === '/demo';
  const isDemo2 = location.pathname === '/demo2';
  const isAnyDemo = isDemo || isDemo2;
  const isLandingPage = location.pathname === '/';
  const isTryonPage = location.pathname.startsWith('/tryon') || location.pathname.startsWith('/studio') || location.pathname === '/style-tinder';

  return (
    <div className={`App ${isTryonPage ? 'App-tryon' : ''} ${isLandingPage ? 'App-landing' : ''}`}>
      <Header
        user={user}
        handleGoogleSignIn={handleGoogleSignIn}
        handleSignOut={handleSignOut}
        lightBackground={isTryonPage || isAnyDemo}
        minimal={isAnyDemo}
        ctaLabel="Sign up"
        isLandingPage={isLandingPage}
    />
      {!isAnyDemo && <Navigation />}
      <Routes>
          <Route
            path="/"
            element={
              <>
                {lazyPage(<HomeScreen user={user} handleGoogleSignIn={handleGoogleSignIn}/>)}
                <Footer />
              </>
            }
        />
          <Route path="/tryon" element={lazyPage(<Tryon />)} />
          <Route path="/studio" element={<AdminOnly>{lazyPage(<Studio />)}</AdminOnly>} />
          <Route path="/studio/lab" element={<AdminOnly>{lazyPage(<StudioLab />)}</AdminOnly>} />
          <Route path="/studio/library" element={<AdminOnly>{lazyPage(<StudioLibrary />)}</AdminOnly>} />
          <Route path="/studio/abc" element={<AdminOnly>{lazyPage(<StudioAbc />)}</AdminOnly>} />
          <Route path="/studio/pins" element={<AdminOnly>{lazyPage(<StudioPins />)}</AdminOnly>} />
          <Route path="/studio/ab" element={<AdminOnly>{lazyPage(<StudioAb2 />)}</AdminOnly>} />
          <Route path="/studio/shoots" element={<AdminOnly>{lazyPage(<StudioShoots />)}</AdminOnly>} />
          <Route path="/studio/sketch" element={<AdminOnly>{lazyPage(<StudioSketch />)}</AdminOnly>} />
          <Route path="/studio/focus" element={<AdminOnly>{lazyPage(<StudioFocus />)}</AdminOnly>} />
          <Route path="/studio/review" element={<AdminOnly>{lazyPage(<StudioReview />)}</AdminOnly>} />
          <Route path="/studio/try20" element={<AdminOnly>{lazyPage(<StudioTry20 />)}</AdminOnly>} />
          <Route path="/studio/swaptest" element={<AdminOnly>{lazyPage(<StudioSwapTest />)}</AdminOnly>} />
          <Route path="/studio/fashion" element={<AdminOnly>{lazyPage(<StudioFashion />)}</AdminOnly>} />
          <Route path="/studio/templates-pick" element={<AdminOnly>{lazyPage(<StudioTemplatePick />)}</AdminOnly>} />
          <Route path="/studio/hub" element={<AdminOnly>{lazyPage(<StudioHub />)}</AdminOnly>} />
          <Route path="/style-tinder" element={<AdminGate>{lazyPage(<StyleTinder />)}</AdminGate>} />
          <Route path="/pricing" element={lazyPage(<Pricing user={user} handleGoogleSignIn={handleGoogleSignIn} />)} />
          <Route path="/admin" element={lazyPage(<Admin />)} />
          <Route path="/uploadtemplates" element={lazyPage(<UploadTemplates />)} />
          <Route path="/demo" element={lazyPage(<Demo />)} />
          <Route path="/demo2" element={lazyPage(<Demo2 />)} />
          <Route path="/demo-manager" element={lazyPage(<DemoManager />)} />
          <Route path="/media" element={lazyPage(<Media />)} />
          <Route path="/legal" element={<>{lazyPage(<Legal/>)}<Footer /></>} />
          <Route path="/api" element={<>{lazyPage(<Api/>)}<Footer /></>} />
          <Route
            path="/tryon/user/:userId/image/:docRefId"
            element={lazyPage(<Tryon />)}
        />
        </Routes>
    </div>
  )
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  )
}

export default App
